import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { showError, showLogoutConfirm, showSessionExpired } from "../../helpers/Toast";

import { Footer } from "../../components";
import { useAuth } from "../../context/AuthContext";
import { biodataUrl } from "../../helpers/ApiPresensi";
import { decryptLocalStorage } from "../../helpers/SecureStorage";

const Profile = () => {
  const { logout, handleSessionExpired } = useAuth();
  const navigate = useNavigate();
  const [biodata, setbiodata] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const token = decryptLocalStorage(process.env.REACT_APP_LOCAL_STORAGE_TOKEN);

  const handleLogout = async () => {
    const result = await showLogoutConfirm();
    if (result.isConfirmed) {
      logout();
    }
  };

  const gotoeditprofile = () => {
    navigate("/edit-profile", {
      state: {
        foto: biodata?.foto,
        nama: biodata?.nama_pegawai,
        alamat: biodata?.alamat,
        email: biodata?.email,
        no_hp: biodata?.no_hp,
      },
    });
  };

  const gotoeditpassword = () => {
    navigate("/edit-password");
  };

  const getbio = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(biodataUrl, {
        method: "GET",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status !== 200) {
        if (response.status === 401) {
          handleSessionExpired();
          await showSessionExpired();
          navigate("/");
          return;
        }
        const data = await response.json();
        showError(data.message || "Terjadi kesalahan");
        return;
      }

      const data = await response.json();
      setbiodata(data.data);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getbio();
  }, []);

  const avatarSrc =
    biodata?.foto === false
      ? `https://ui-avatars.com/api/?background=1277C1&color=fff&name=${encodeURIComponent(biodata?.nama_pegawai || "U")}&size=200`
      : biodata?.foto;

  return (
    <div className="flex flex-col min-h-screen overflow-x-hidden bg-gray-50">
      <Helmet>
        <title>Profil — Presensi</title>
      </Helmet>

      <div className="flex-grow">
        {/* Header gradient */}
        <div
          className="h-36 w-full"
          style={{ background: "linear-gradient(135deg, #FFB703 0%, #f5a800 100%)" }}
        />

        {/* Avatar */}
        <div className="flex flex-col items-center" style={{ marginTop: -56 }}>
          {isLoading ? (
            <div className="w-28 h-28 rounded-full bg-gray-200 border-4 border-white shadow-lg animate-pulse" />
          ) : (
            <img
              src={avatarSrc}
              className="w-28 h-28 rounded-full object-cover border-4 border-white shadow-lg"
              alt="Foto Profil"
            />
          )}

          <div className="mt-3 text-center px-4">
            {isLoading ? (
              <>
                <div className="h-5 w-40 bg-gray-200 rounded-lg mx-auto animate-pulse mb-2" />
                <div className="h-4 w-28 bg-gray-200 rounded-lg mx-auto animate-pulse" />
              </>
            ) : (
              <>
                <h1 className="text-xl font-bold text-gray-800">{biodata?.nama_pegawai}</h1>
                <span className="inline-block mt-1.5 text-xs font-medium text-yellow-700 bg-yellow-100 rounded-full px-3 py-0.5">
                  {biodata?.jenis_pegawai}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Info Card */}
        {!isLoading && (biodata?.email || biodata?.no_hp || biodata?.alamat) && (
          <div className="mx-4 mt-5 bg-white rounded-2xl shadow-sm border border-gray-100 divide-y divide-gray-100">
            {biodata?.email && (
              <div className="flex items-center gap-3 px-4 py-3">
                <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                  <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Email</p>
                  <p className="text-sm font-medium text-gray-700">{biodata.email}</p>
                </div>
              </div>
            )}
            {biodata?.no_hp && (
              <div className="flex items-center gap-3 px-4 py-3">
                <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center shrink-0">
                  <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-gray-400">No. HP</p>
                  <p className="text-sm font-medium text-gray-700">{biodata.no_hp}</p>
                </div>
              </div>
            )}
            {biodata?.alamat && (
              <div className="flex items-center gap-3 px-4 py-3">
                <div className="w-8 h-8 rounded-full bg-yellow-50 flex items-center justify-center shrink-0">
                  <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Alamat</p>
                  <p className="text-sm font-medium text-gray-700">{biodata.alamat}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="mx-4 mt-5 space-y-2.5">
          <button
            onClick={gotoeditprofile}
            className="w-full flex items-center gap-3 px-4 py-3.5 bg-white rounded-2xl shadow-sm border border-gray-100 text-left hover:bg-gray-50 transition"
          >
            <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
              <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <span className="flex-1 text-sm font-semibold text-gray-700">Edit Profil</span>
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <button
            onClick={gotoeditpassword}
            className="w-full flex items-center gap-3 px-4 py-3.5 bg-white rounded-2xl shadow-sm border border-gray-100 text-left hover:bg-gray-50 transition"
          >
            <div className="w-9 h-9 rounded-full bg-purple-50 flex items-center justify-center shrink-0">
              <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            </div>
            <span className="flex-1 text-sm font-semibold text-gray-700">Ubah Password</span>
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3.5 bg-red-50 rounded-2xl shadow-sm border border-red-100 text-left hover:bg-red-100 transition"
          >
            <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center shrink-0">
              <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </div>
            <span className="flex-1 text-sm font-semibold text-red-600">Keluar</span>
          </button>
        </div>

        <div className="mt-6 mb-3 text-center space-y-0.5">
          <p className="text-xs text-gray-400">Presensi Al Irsyad Surakarta · v1.5</p>
          <p className="text-xs text-gray-400">Dikembangkan oleh <span className="font-medium text-gray-500">CV Prabu Bima Tech</span></p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Profile;
